/**
 * PIN IT EDITIONS (job #941, Tom 2026-10-10): "There's no reason to keep the games interface in English if we HAVE the
 * localised versions." Pin it in the player's own language at /guess/<code>/pin-it, the codes three letters like the
 * courses. The voices are the same clips; what changes is every word around them: the game's text, the language and
 * place names, the tells, the share, and the learn link, which goes to the English course for that language.
 *
 * A leaf module (the router and tools/pin-editions/extract.mjs read it), so it imports nothing.
 */
export interface PinEdition {
  /** ISO 639-3, the route code and the interface locale it answers to. */
  code: string
  /** BCP 47, for lang= and for numbers. */
  bcp47: string
  /** Its name in itself. */
  endonym: string
  /** Written right to left. */
  rtl?: boolean
  /** The course its players are sent to: English for speakers of it. */
  course: string
  /**
   * The clip languages (guessData keys) its players already speak, never dealt to them (job #992): their own language
   * first, then its varieties and the spoken twin they would take for it. Keys the data lacks are harmless.
   */
  own: string[]
}

/**
 * Close variants are one language to the player's ear: the Spanish, German and Arabic varieties, and Hindi with Urdu
 * (one spoken language). Cantonese stays in the Chinese edition: a different language to a Mandarin speaker's ear.
 */
const OWN: Record<string, string[]> = {
  urd: ['urd', 'hin'], hin: ['hin', 'urd'], spa: ['spa', 'spa_mx'], fra: ['fra', 'fra_ca'], por: ['por', 'por_br'],
  deu: ['deu', 'deu_at', 'deu_ch'], ara: ['ara', 'ara_sy', 'ara_lb', 'ara_eg'],
}
const ed = (code: string, bcp47: string, endonym: string, rtl = false): PinEdition => ({ code, bcp47, endonym, course: `eng_for_${code}`, own: OWN[code] ?? [code], ...(rtl ? { rtl } : {}) })

/** The ten Indian languages of the eng_for_* courses, then the seven big ones. Every course named here exists (checked live 2026-10-10). */
export const PIN_EDITION_LIST: PinEdition[] = [
  ed('hin', 'hi', 'हिन्दी'), ed('tam', 'ta', 'தமிழ்'), ed('ben', 'bn', 'বাংলা'), ed('guj', 'gu', 'ગુજરાતી'), ed('pan', 'pa', 'ਪੰਜਾਬੀ'),
  ed('urd', 'ur', 'اردو', true), ed('mar', 'mr', 'मराठी'), ed('tel', 'te', 'తెలుగు'), ed('kan', 'kn', 'ಕನ್ನಡ'), ed('sin', 'si', 'සිංහල'),
  ed('spa', 'es', 'Español'), ed('fra', 'fr', 'Français'), ed('deu', 'de', 'Deutsch'), ed('por', 'pt', 'Português'),
  ed('ara', 'ar', 'العربية', true), ed('zho', 'zh-Hans', '中文'), ed('jpn', 'ja', '日本語'),
]
export const PIN_EDITIONS = PIN_EDITION_LIST.map((e) => e.code)

/** The edition for a code, or null. */
export const pinEdition = (code: unknown): PinEdition | null => PIN_EDITION_LIST.find((e) => e.code === code) ?? null

/** An edition's address: /guess/hin/pin-it. */
export const pinEditionPath = (code: string): string => `/guess/${code}/pin-it`

/**
 * The game-text ids Pin it can show (gameDefaults.ts), the ones every edition translates. editions.test.ts reads the
 * game's source for every gt('…') id it asks for and fails on any not listed here.
 */
export const PIN_GAME_IDS = [
  'game_pin_title', 'game_pin_lead', 'hub_pin_tag', 'map_title', 'more_games', 'btn_play', 'btn_next', 'btn_play_again',
  'audio_failed', 'audio_retry', 'play_again_aria', 'question_of',
  'pin_hint', 'pin_btn', 'pin_aria', 'pin_zoom_in', 'pin_zoom_out', 'pin_whole',
  'pin_line_spot', 'pin_line_close', 'pin_line_near', 'pin_line_region', 'pin_line_far',
  'pin_it_was', 'pin_spoken_here', 'pin_inside', 'pin_inside_short', 'pin_ring_bull', 'pin_ring_within', 'pin_ring_out', 'pin_km',
  'challenge_btn', 'challenge_pin_fixed_line', 'challenge_pin_line', 'challenge_who_default', 'challenge_you', 'challenge_rows_aria',
  'share_name_label', 'share_name_hint', 'share_pin_count', 'share_pin_beat', 'share_shared', 'share_copied_link', 'share_failed',
  'share_btn_share', 'share_btn_copy', 'versus_win', 'versus_level', 'versus_lose',
  'and_word', 'mirror_best', 'mirror_pin_within', 'mirror_pin_closest', 'mirror_pin_furthest', 'recognise_now', 'recognise_more',
  'level_reached', 'level_1', 'level_2', 'level_3', 'level_4', 'level_5',
  'edition_learn',
] as const
export type PinGameId = (typeof PIN_GAME_IDS)[number]
